import axios from "axios";
import {
    IRDByThematicIds,
    RDForT1,
    RDForT1Schema,
    Site,
    SiteInfraGridSchema,
    SiteSchema,
    SiteSeoGridSchema,
    SiteTrafficGridSchema,
    UpsertContentSchema,
    UpsertSiteDTO,
} from "../types/Site";
import { toCamelCase, toSnakeCase } from "../utils/objectHelper";
import qs from "qs";
import { SimpleFormSelectOption } from "../components/form/SimpleFormSelect";
import { UserModel } from "../types/UserModel";
import { rdAvailable, rdAvailableSchema } from "../types/rdAvailable";

export const SiteTransport = {
    offSiteStats: async (moneySiteId: number) => {
        const { data } = await axios.get(`/statsSite/offSiteStats/${moneySiteId}`, {});
        return data;
    },
    onSiteStats: async (moneySiteId: number) => {
        const { data } = await axios.get(`/statsSite/offSiteStats/${moneySiteId}`, {});
        return data;
    },
    getSiteById: async (siteId: number) => {
        const { data } = await axios.get(`/site/${siteId}`, {});
        return SiteSchema.cast(toCamelCase(data), { stripUnknown: true });
    },

    addManagers: async (managerIds: number[], siteIds: number[]) => {
        const { data } = await axios.put("/sites/addManagers", { managerIds, siteIds });
        return data;
    },

    // used in NLGridsByMonthContainer
    list: async (params?: object) => {
        const { data } = await axios.get(
            `/sites/manager${params ? `?${qs.stringify(params)}` : ""}`,
            {}
        );
        return data.map((current: any) => {
            return toCamelCase(current);
        });
    },

    //used in UpsertRDOrderGrid
    listAvailableByRD: async (params?: object): Promise<Array<rdAvailable>> => {
        const { data } = await axios.get(
            `/sites/rd${params ? `?${qs.stringify(params)}` : ""}`,
            {}
        );
        return data.map((current: any) => {
            return rdAvailableSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listAllRD: async () => {
        const { data } = await axios.get(`/sites/allRD`, {});
        return data.map((current: any) => {
            return toCamelCase(current);
        });
    },

    listTraffic: async () => {
        const { data } = await axios.get(`/sites/withKwStats`, {});
        return data.map((el: any) =>
            SiteTrafficGridSchema.cast(toCamelCase(el), { stripUnknown: true })
        );
    },

    // use in SiteSEO page
    listSeo: async (params?: any) => {
        const { data } = await axios.get<Site[]>(
            `/sites/seo${params ? `?${qs.stringify(params)}` : ""}`
        );
        return data.map((current: any) => {
            return SiteSeoGridSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    // use in SiteSEO page
    getUptimeStatus: async () => {
        const { data } = await axios.get<any[]>("/sites/listUptimeStatus");

        return data;
    },

    // use in Infra Site page
    listInfra: async (params?: any) => {
        const { data } = await axios.get<Site[]>(
            `/sites/infra${params ? `?${qs.stringify(params)}` : ""}`
        );
        return data.map((current: any) => {
            return SiteInfraGridSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    listAvailableRDForT1: async (moneySiteId: number): Promise<Array<RDForT1>> => {
        const { data } = await axios.get<Array<RDForT1>>(
            `/sites/findAvailableRDForT1?moneySiteId=${moneySiteId}`
        );
        return data.map((current: RDForT1) => {
            return RDForT1Schema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    // used in upsertOrder
    optionsAll: async (): Promise<Array<SimpleFormSelectOption>> => {
        const { data } = await axios.get(`/sites/options/all`, {});
        return data.map((current: any) => {
            return toCamelCase(current);
        });
    },

    // use in orderTextContainer and SiteAnalyzeManagementWrapper
    optionsAllPrivateByManagerAndUnder: async (
        params?: any
    ): Promise<Array<SimpleFormSelectOption>> => {
        const { data } = await axios.get<SimpleFormSelectOption[]>(
            `/sites/options/private${params ? `?${qs.stringify(params)}` : ""}`
        );
        return data;
    },

    optionsByProjectManager: async (id?: number): Promise<Array<SimpleFormSelectOption>> => {
        const { data } = await axios.get(`/sites/options/manager/${id}`, {});
        return data;
    },

    optionsByManager: async (): Promise<Array<SimpleFormSelectOption>> => {
        const { data } = await axios.get(`/sites/options/byManager`, {});
        return data;
    },

    getAuthCode: async (id?: number): Promise<string> => {
        const { data } = await axios.get(`/sites/authCode/${id}`, {});
        return data;
    },

    toggleActive: async (object: Site): Promise<void> => {
        const action = "toggleActive";
        await axios.patch(`/sites/${object.id}/actions/${action}`);
    },

    RDByThematicIds: async (
        moneySiteId: number,
        thematicId: Array<number>
    ): Promise<Array<IRDByThematicIds>> => {
        const { data } = await axios.get<Array<IRDByThematicIds>>(
            `/sites/rdByThematicsIds?moneySiteId=${moneySiteId}&thematicIds=${thematicId.toString()}`
        );
        return data.map((current) => {
            return toCamelCase(current);
        });
    },

    findAvailableRDForT1ByThematic: async (
        moneySiteId: number,
        thematicId: number,
        rdNeeded: number
    ): Promise<Array<RDForT1>> => {
        const { data } = await axios.get<Array<RDForT1>>(
            `/sites/findAvailableRDForT1ByThematic?moneySiteId=${moneySiteId}&thematicId=${thematicId}&rdNeeded=${rdNeeded}`
        );
        return data.map((current) => {
            return toCamelCase(current);
        });
    },

    save: async (
        object: UpsertSiteDTO,
        id: number | undefined | null,
        confirmationResponse?: any
    ): Promise<Site> => {
        const formattedObject = {
            ...object,
            domain: object.domain.toLocaleLowerCase(),
        };

        if (confirmationResponse) {
            formattedObject.registrarAccountId = confirmationResponse?.registrarAccount?.id;
        }
        const { data } = await axios.request({
            method: id ? "put" : "post",
            url: id ? `/sites/${id}` : "/sites",
            data: toSnakeCase(formattedObject),
        });

        return SiteSchema.cast(toCamelCase(data), { stripUnknown: true });
    },

    removeProjectManagers: async (siteIds: Array<number>): Promise<Site> => {
        const { data } = await axios.request({
            method: "put",
            url: `/sites/removeProjectManagers`,
            data: { siteIds },
        });

        return data.map((currentValue: any) =>
            SiteSchema.cast(toCamelCase(currentValue), { stripUnknown: true })
        );
    },

    buyDomain: async (object: Site): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/buyDomain`,
        });

        return response.data;
    },
    metrics: async (object: Site): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/getMetrics`,
        });

        return response.data;
    },
    createZone: async (object: Site): Promise<{ id: string; nameServers: string[] }> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/createZone`,
        });

        return response.data;
    },
    disableIPV6: async (object: Site, zoneId: string): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/disableIPV6/${zoneId}`,
        });

        return response.data;
    },
    cleanDNS: async (object: Site, zoneId: string): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/cleanDNS/${zoneId}`,
        });

        return response.data;
    },
    addDNS: async (object: Site, zoneId: string): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/addDNS/${zoneId}`,
        });

        return response.data;
    },

    addNameServers: async (object: Site, nameSpaces: string[]): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/addNameServers`,
            data: nameSpaces,
        });

        return response.data;
    },
    addSSL: async (object: Site, zoneId: string): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/addSSL/${zoneId}`,
        });

        return response.data;
    },
    generatePostByDefault: async (object: Site): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/generatePostByDefault`,
        });

        return response.data;
    },
    askToInstallWordPress: async (site: Site): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${site.id}/askToInstallWordPress`,
        });

        return response.data;
    },
    createUpTimeCheck: async (object: Site): Promise<Site> => {
        const response = await axios.request({
            method: "post",
            url: `/sites/${object.id}/createUpTime`,
        });
        return response.data;
    },

    saveBulk: async (sites: Array<UpsertSiteDTO> | any): Promise<Array<Site>> => {
        const formattedData = {
            sites: {
                ...sites,
            },
        };
        const { data } = await axios.request({
            method: "put",
            url: `/sites/bulk`,
            data: toSnakeCase(formattedData),
        });
        return data.map((current: any) => {
            return SiteSchema.cast(toCamelCase(current), { stripUnknown: true });
        });
    },

    wordpressAccess: async (moneySiteId: number): Promise<any> => {
        const response = await axios.request({
            method: "post",
            url: `/site/wordpressAccess?moneySiteId=${moneySiteId}`,
        });
        return response;
    },

    updateMetrics: async (ids: number[], metrics: Array<string>): Promise<any> => {
        const response = await axios.request({
            method: "post",
            url: `/metrics/bulkUpdate?ids=${ids.join(",")}&metrics=${metrics.join(",")}`,
        });
        return response;
    },

    checkDomain: async (object: UpsertSiteDTO): Promise<any> => {
        const formattedObject = {
            ...object,
            domain: object.domain.toLocaleLowerCase(),
        };
        const response = await axios.request({
            method: "post",
            url: `/sites/checkDomain`,
            data: toSnakeCase(formattedObject),
        });
        return response.data;
    },

    monitoring: {
        tf: async (): Promise<any> => {
            const { data } = await axios.request({
                method: "get",
                url: `/sites/monitoring/tf`,
            });
            return data;
        },
        traffic: async (): Promise<any> => {
            const { data } = await axios.request({
                method: "get",
                url: `/sites/monitoring/traffic`,
            });
            return data;
        },
        kw: async (): Promise<any> => {
            const { data } = await axios.request({
                method: "get",
                url: `/sites/monitoring/kw`,
            });
            return data;
        },
        gtMetrix: async (): Promise<any> => {
            const { data } = await axios.request({
                method: "get",
                url: `/sites/monitoring/gtMetrix`,
            });
            return data;
        },
        filling: async (): Promise<any> => {
            const { data } = await axios.request({
                method: "get",
                url: `/sites/monitoring/filling`,
            });
            return data;
        },
    },

    formTransformer: (object: Site): UpsertSiteDTO => {
        return {
            domain: object.domain,
            buyDomain: object?.buyDomain,
            needCategories: object.needCategories,
            managerIds: object.managers?.map((user: UserModel) => user.id),
            categories: object.categories,
            postByDefault: false,
            persona: object.persona,
            priceBought: object.priceBought,
            linkerId: object.linker?.id,
            adminId: object.admin?.id,
            mt: object.mt,
            content: object.content ? object.content : UpsertContentSchema.getDefault(),
            lang: object.lang,
            managedByThirdParty: !object.registrarAccount,
            thematicId: object.thematic.id,
            registrarAccountId: object.registrarAccount?.id,
            pbnId: object.pbn.id,
        };
    },

    getMetrics: () => {
        return [
            "TF",
            "CF",
            "RD",
            "TTF",
            "TRAFFIC",
            "KW",
            "GOOGLE_RESULT",
            "UNIQUE_VISITORS",
            "PAGE_SPEED",
        ];
    },
};
